"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { ArticleCard, ArticleHeroCard, ArticleRow, ArticleTopicChips, WriterCtaBand, type ArticleListItem } from "@/components/articles/article-cards";
import { ARTICLE_TOPICS, getArticleTopic, isArticleTopicKey, type ArticleTopicKey } from "@/lib/articles/topics";

// ─── Articles index content ──────────────────────────────
// The ?topic=… filterable half of /articles. The page itself is statically
// rendered (ISR) with the full article list embedded; the active topic is read
// from the URL here, client-side, so topic filtering never invokes a server
// function. useSearchParams lives inside the Suspense boundary — the fallback
// (and therefore the static HTML) is the unfiltered magazine view.

export function ArticlesIndexContent(t0) {
  const $ = _c(2);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = <Suspense fallback={<ContentView items={items} activeTopic={null} />}><ContentWithParams items={items} /></Suspense>;
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ContentWithParams(t0) {
  const $ = _c(5);
  const {
    items
  } = t0;
  const sp = useSearchParams();
  let t1;
  if ($[0] !== sp) {
    const topicParam = sp.get("topic");
    t1 = topicParam && isArticleTopicKey(topicParam) ? topicParam : null;
    $[0] = sp;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeTopic = t1;
  let t2;
  if ($[2] !== activeTopic || $[3] !== items) {
    t2 = <ContentView items={items} activeTopic={activeTopic} />;
    $[2] = activeTopic;
    $[3] = items;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function TopicSectionHeader(t0) {
  const $ = _c(16);
  const {
    topicKey,
    count
  } = t0;
  let t1;
  if ($[0] !== topicKey) {
    t1 = getArticleTopic(topicKey);
    $[0] = topicKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const topic = t1;
  let t2;
  if ($[2] !== topic.label) {
    t2 = <h2 className="font-heading text-xl font-bold tracking-tight text-foreground sm:text-2xl">{topic.label}</h2>;
    $[2] = topic.label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = typeof count === "number" && ` · ${count} article${count === 1 ? "" : "s"}`;
  let t4;
  if ($[4] !== t3 || $[5] !== topic.intent) {
    t4 = <p className="mt-1 text-sm text-muted-foreground">{topic.intent}{t3}</p>;
    $[4] = t3;
    $[5] = topic.intent;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <div>{t2}{t4}</div>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== count || $[11] !== topicKey) {
    t6 = typeof count !== "number" && <Link href={`/articles?topic=${topicKey}`} className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary/80">View all<ArrowRight className="size-3.5" /></Link>;
    $[10] = count;
    $[11] = topicKey;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="mb-6 flex flex-wrap items-end justify-between gap-3">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
function ContentView(t0) {
  const $ = _c(13);
  const {
    items,
    activeTopic
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== activeTopic || $[1] !== items) {
    const editorial = items.filter(_temp);
    const hero = editorial[0] ?? items[0];
    const picks = editorial.filter(a_0 => a_0 !== hero).slice(0, 3);
    const featuredSlugs = new Set(hero ? [hero.slug, ...picks.map(_temp2)] : []);
    const rest = items.filter(a_2 => !featuredSlugs.has(a_2.slug));
    const filtered = activeTopic ? items.filter(a_3 => a_3.topic === activeTopic) : items;
    const topicSections = ARTICLE_TOPICS.map(topic => ({
      key: topic.key,
      articles: rest.filter(a_4 => a_4.topic === topic.key).slice(0, 3)
    })).filter(_temp3);
    if ($[5] !== activeTopic) {
      t3 = <ArticleTopicChips activeTopic={activeTopic} />;
      $[5] = activeTopic;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t1 = "mt-10";
    t2 = items.length === 0 ? <div className="rounded-xl border border-dashed border-border bg-card/50 p-12 text-center"><p className="text-muted-foreground">No articles published yet — check back soon.</p></div> : activeTopic ? <div className="space-y-16"><section><TopicSectionHeader topicKey={activeTopic} count={filtered.length} />{filtered.length === 0 ? <div className="rounded-xl border border-dashed border-border bg-card/50 p-12 text-center"><p className="text-muted-foreground">Nothing here yet — check back soon.</p></div> : <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{filtered.map(_temp4)}</div>}</section><WriterCtaBand /></div> : <div className="space-y-16"><section aria-label="Featured article">{hero && <ArticleHeroCard item={hero} />}{picks.length > 0 && <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{picks.map(_temp5)}</div>}</section>{topicSections.map(_temp7)}<section><div className="mb-2 flex items-baseline justify-between"><h2 className="font-heading text-xl font-bold tracking-tight text-foreground sm:text-2xl">All articles</h2><span className="text-sm text-muted-foreground">{items.length} article{items.length === 1 ? "" : "s"}</span></div><div>{items.map(_temp8)}</div></section>{topicSections.length < 2 && <WriterCtaBand />}</div>;
    $[0] = activeTopic;
    $[1] = items;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2) {
    t4 = <div className={t1}>{t2}</div>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
function _temp8(a_8) {
  return <ArticleRow key={a_8.slug} item={a_8} />;
}
function _temp7(section, i) {
  return <div key={section.key} className="space-y-16"><section><TopicSectionHeader topicKey={section.key} /><div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{section.articles.map(_temp6)}</div></section>{i === 1 && <WriterCtaBand />}</div>;
}
function _temp6(a_7) {
  return <ArticleCard key={a_7.slug} item={a_7} />;
}
function _temp5(a_6) {
  return <ArticleCard key={a_6.slug} item={a_6} />;
}
function _temp4(a_5) {
  return <ArticleCard key={a_5.slug} item={a_5} />;
}
function _temp3(s) {
  return s.articles.length > 0;
}
function _temp2(a_1) {
  return a_1.slug;
}
function _temp(a) {
  return a.topic !== "guides";
}